import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  applySurveyTemplate,
  saveSurveyToTemplate,
} from 'api/administration/blocks/SurveyTemplates'
import lessonBlocksList from 'api/administration/blocks/List'
import { getQuestionList } from 'api/administration/questions/List'
import { useNotification } from 'hooks/notifications/useNotification'
import { IBlockInstanceModel } from 'models/block.model'
import { BlockTypes } from 'enum/block-type.enum'
import { readValidationErrors } from 'pages/administration/SurveyBank/common/errors'
import type { SurveyTemplateRow } from 'pages/administration/SurveyBank/common/types'
import { TRANSLATION_PREFIX } from 'pages/administration/SurveyBank/constants'
import * as fromActions from 'store/actions/blocks.action'
import * as fromQuestionActions from 'store/actions/questions.action'
import { IMainState } from 'store/states'

/**
 * Survey bank actions of a course survey block: whether it has questions,
 * using a survey of the bank and saving the survey to the bank.
 */
export const useSurveyBankActions = (edit: IBlockInstanceModel) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notification = useNotification()
  const { list } = useSelector(({ questions }: IMainState) => questions)
  // `undefined` is still loading. `null` is a failed read: neither zero
  // questions nor a survey that already has some.
  const [questionCount, setQuestionCount] = useState<number | null>()
  const [applying, setApplying] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveNameError, setSaveNameError] = useState<string | null>(null)

  const blockSurveyId = edit.id
  const instanceId = edit.instanceId
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`${TRANSLATION_PREFIX}.block.${key}`, options)

  // Read through a ref so the count effect depends only on the block and list.
  const notifyCountError = useRef(() => undefined as void)
  notifyCountError.current = () => {
    notification.error(t('administration.toastMessages.genericError'))
  }

  // Counted from the API, not from the shared question list in the store,
  // which may still hold another block's questions; it is counted again
  // whenever the editor changes that list.
  useEffect(() => {
    if (!instanceId) {
      setQuestionCount(undefined)
      return
    }
    let active = true
    getQuestionList(instanceId)
      .then(
        (questions: unknown[]) => active && setQuestionCount(questions.length)
      )
      .catch(() => {
        if (!active) return
        setQuestionCount(null)
        notifyCountError.current()
      })
    return () => {
      active = false
    }
  }, [instanceId, list])

  const knownCount =
    typeof questionCount === 'number' ? questionCount : undefined
  const questionsReady = !!blockSurveyId && knownCount !== undefined
  const hasQuestions = questionsReady && (knownCount ?? 0) > 0

  const refreshBlock = (changes: Partial<IBlockInstanceModel>) => {
    dispatch({
      type: fromActions.EDIT_BLOCK,
      payload: { ...edit, ...changes, blockType: BlockTypes.BlockSurvey },
    })
    // Fetched here instead of through GET_LESSON_BLOCKS_LIST: that action turns
    // on the lesson loader, which remounts this editor back on step 1.
    if (edit.lessonId)
      lessonBlocksList(edit.lessonId)
        .then((blocks) =>
          dispatch(fromActions.Actions.getLessonBlocksListSuccess(blocks))
        )
        .catch(() => undefined)
    dispatch({ type: fromQuestionActions.GET_QUESTIONS_LIST, instanceId })
  }

  /** Copies `row` into the block; resolves true when the copy was saved. */
  const applyTemplate = async (row: SurveyTemplateRow) => {
    if (!blockSurveyId) return false
    setApplying(true)
    try {
      await applySurveyTemplate(blockSurveyId, row.id)
      refreshBlock({ name: row.name, description: row.description ?? '' })
      if (instanceId)
        getQuestionList(instanceId)
          .then((questions: unknown[]) => setQuestionCount(questions.length))
          .catch(() => undefined)
      notification.success(text('applied', { name: row.name }))
      return true
    } catch (error) {
      notification.error(
        text(
          readValidationErrors(error)
            ? 'errors.useRejected'
            : 'errors.useFailed'
        )
      )
      return false
    } finally {
      setApplying(false)
    }
  }

  /** Saves the block as a new survey of the bank; true when it was created. */
  const saveToBank = async (values: { name: string; description: string }) => {
    if (!blockSurveyId) return false
    setSaving(true)
    setSaveNameError(null)
    try {
      await saveSurveyToTemplate(blockSurveyId, values)
      notification.success(text('savedTemplate', { name: values.name }))
      return true
    } catch (error) {
      const validation = readValidationErrors(error)
      if (validation?.name) setSaveNameError(text('errors.duplicateName'))
      else notification.error(text('errors.saveFailed'))
      return false
    } finally {
      setSaving(false)
    }
  }

  return {
    questionsReady,
    canUseBank: questionsReady && knownCount === 0,
    canSaveToBank: hasQuestions,
    applying,
    saving,
    saveNameError,
    clearSaveNameError: () => setSaveNameError(null),
    applyTemplate,
    saveToBank,
  }
}
